import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { formatDate, usePathParam, usePagination } from '@chilecompra/react-kit'
import { Card, Skeleton, Table, TableRow, Toast, Typography, Pagination } from '@chilecompra/react-kit/components'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import { onGetTechnicalSuitabilityThunk } from './TechnicalSuitabilitySection.actions'
import { Column, Content, Row, Separator, TableCell } from './TechnicalSuitabilitySection.styles'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'

/**
 * The Technical Suitability section container.
 */
const TechnicalSuitabilitySection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const isForeign = checkIfForeignDni(dni)
  const { activityStartDate, error, loading } = useSelector(state => state.technicalSuitability)
  const { sellerType } = useSelector(state => state.basicSellerInformation)
  const { businessActivities, error: siiError, loading: siiLoading } = useSelector(state => state.siiInformation)

  useEffect(() => {
    if (!loading) {
      return
    }

    if (sellerType !== '' && !isForeign) {
      dispatch(onGetTechnicalSuitabilityThunk({ dni }))
    }
  }, [sellerType])
  const rowsPerPage = 10
  const { page, totalPages, onChangePage, onGetCurrentPage, records } = usePagination({
    data: businessActivities,
    initialPage: 1,
    initialRowsPerPage: rowsPerPage
  })

  return (
    <Content>
      <Card color="blank" enableBorder padding="32px 24px 8px">
        <Row>
          <Column width="200px">
            <Typography fontWeight="bold" padding="0 0 3px" variant="h4">
              Actividades económicas
            </Typography>
            <Typography variant="subtitle">Fuente: Servicio de Impuestos Internos</Typography>
          </Column>
          {!isForeign && (
            <Column flexGrow={2} flexBasis="60%">
              <Table accessibility={{ label: 'Situación financiera' }}>
                <ProtectByFeatures rule={features => features['com.seller/technical.activity.start.date']}>
                  <TableRow>
                    <TableCell enableHead width="150px" padding="0 8px 8px 0">
                      Inicio de actividades
                    </TableCell>
                    <TableCell padding="0 0 8px 8px">
                      <Typography variant="body2">{loading ? <Skeleton width="100%" /> : activityStartDate}</Typography>
                    </TableCell>
                  </TableRow>
                </ProtectByFeatures>
                <TableRow>
                  <TableCell colSpan={2} disableBottomBorder padding="12px 0 16px 0">
                    {siiLoading && (
                      <>
                        <Typography fontWeight="bold" padding="0 0 16px" variant="body2">
                          Código y actividad económica SII
                        </Typography>
                        <Skeleton height="30px" variant="rect" width="100%" />
                      </>
                    )}
                    {!siiLoading && !siiError && (
                      <Table accessibility={{ label: 'Actividades económicas' }}>
                        <TableRow>
                          <TableCell
                            disableBottomBorder={businessActivities.length > 0}
                            enableHead
                            padding={businessActivities.length > 0 ? '0 0 8px' : '0 0 23px 3px'}
                          >
                            Código y actividad económica SII
                          </TableCell>
                          <TableCell
                            alignment="right"
                            disableBottomBorder={businessActivities.length > 0}
                            enableHead
                            padding={businessActivities.length > 0 ? '0 0 8px' : '0 0 23px 3px'}
                            width="80px"
                          >
                            Inicio
                          </TableCell>
                        </TableRow>
                        {businessActivities.length > 0 &&
                          onGetCurrentPage().map((businessActivities, index) => {
                            return (
                              <TableRow key={`businessActivities-${index}`}>
                                <TableCell disableBottomBorder padding="0 5px 9px 0">
                                  <Typography color="black3" display="initial" padding="0 6px 0 0" variant="body2">
                                    {businessActivities.code}
                                  </Typography>
                                  {businessActivities.description}
                                </TableCell>
                                <TableCell alignment="right" disableBottomBorder padding="0 0 9px 0" width="80px">
                                  {formatDate(businessActivities.startDate, 'dd-MM-yyyy')}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                      </Table>
                    )}
                    {businessActivities.length > rowsPerPage && <Separator />}
                    {businessActivities.length === 0 && !error && !siiError && (
                      <Table accessibility={{ label: 'Actividades económicas' }}>
                        <TableRow>
                          <TableCell
                            disableBottomBorder={businessActivities.length > 0}
                            enableHead
                            padding={businessActivities.length > 0 ? '0 0 8px' : '0 0 23px 3px'}
                          >
                            Código y actividad económica SII
                          </TableCell>
                          <TableCell
                            alignment="right"
                            disableBottomBorder={businessActivities.length > 0}
                            enableHead
                            padding={businessActivities.length > 0 ? '0 0 8px' : '0 0 23px 3px'}
                            width="80px"
                          >
                            Inicio
                          </TableCell>
                        </TableRow>
                        <TableCell disableBottomBorder padding="0 5px 9px 0">
                          <Typography fontSize="12px" variant="subtitle" padding="8px 0 0 5px">
                            Por el momento no contamos con información de esta empresa proveedora.
                          </Typography>
                        </TableCell>
                      </Table>
                    )}
                    {!siiLoading && !loading && siiError && (
                      <Toast
                        disableBackground
                        margin="0 0 0 -17px"
                        severity="default"
                        title="Lo sentimos, estamos presentando problemas con nuestro servidor."
                      >
                        En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en
                        unos minutos más.
                      </Toast>
                    )}
                  </TableCell>
                </TableRow>
              </Table>
              {!loading && totalPages > 1 && (
                <Pagination
                  alignment="right"
                  size="small"
                  disableFirstButton={false}
                  disableLastButton={false}
                  disableResults={false}
                  labels={{
                    of: 'de',
                    results: 'resultados',
                    showing: 'Mostrando'
                  }}
                  total={businessActivities.length}
                  records={records}
                  onChange={onChangePage}
                  count={totalPages}
                  page={page}
                />
              )}
            </Column>
          )}
          {isForeign && (
            <Column flexBasis="60%" flexGrow={2}>
              <Table accessibility={{ label: 'Información del proveedor' }}>
                <TableRow>
                  <TableCell padding="0 0 8px">
                    <Typography variant="body2">Sin información disponible.</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell disableBottomBorder padding="12px 0 16px">
                    <Typography variant="subtitle">
                      No tenemos información disponible de esta empresa proveedora extranjera.
                    </Typography>
                  </TableCell>
                </TableRow>
              </Table>
            </Column>
          )}
        </Row>
      </Card>
    </Content>
  )
}

export default TechnicalSuitabilitySection
